import React from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Avatar, List, ListItem, 
  ListItemText, ListItemIcon, Divider, LinearProgress, Stack
} from '@mui/material';
import {
  CheckCircle, Warning, Description, QrCodeScanner, FiberManualRecord,
  ChevronRight, ListAlt, AssignmentTurnedIn
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import axios from 'axios';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const DashboardResumen = () => {
  const { data: stats, isLoading } = useQuery(['dashboard', 'stats'], async () => {
    const res = await axios.get('/api/dashboard/stats');
    return res.data;
  });

  if (isLoading) return <Box sx={{ p: 4, mt: 8 }}><LinearProgress /></Box>;

  // Data for Donut Chart
  const pieData = stats?.censo?.map(item => ({
    name: item.especie,
    value: parseInt(item.count)
  })) || [];

  const COLORS = ['#10b981', '#f59e0b', '#3b82f6', '#be123c'];

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: 8, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <CheckCircle sx={{ fontSize: 48, color: 'var(--emerald-main)' }} />
        <Typography variant="h3" sx={{ fontWeight: 900, color: 'var(--navy-main)', letterSpacing: '-0.04em' }}>
          Resumen de Explotación
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* 1. Alert Card (Restricción de Salida) */}
        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              height: '100%', 
              bgcolor: stats?.restriccionSalida ? '#fee2e2' : '#f0fdf4', 
              borderRadius: '24px', 
              boxShadow: 'none', 
              border: stats?.restriccionSalida ? '1px solid #fecaca' : '1px solid #dcfce7',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              p: 3,
              textAlign: 'center'
            }}
          >
            {stats?.restriccionSalida ? (
              <>
                <Typography variant="h5" sx={{ color: '#991b1b', fontWeight: 900, mb: 1 }}>
                  ¡ALERTA!
                </Typography>
                <Typography variant="h6" sx={{ color: '#991b1b', fontWeight: 700, mb: 4 }}>
                  Restricción de salida activa<br/>
                  <Typography component="span" variant="body1" sx={{ opacity: 0.8 }}>(Sanitaria Crítica)</Typography>
                </Typography>
                <Warning sx={{ fontSize: 120, color: '#ef4444' }} />
              </>
            ) : (
              <>
                <CheckCircle sx={{ fontSize: 120, color: '#22c55e', mb: 2 }} />
                <Typography variant="h5" sx={{ color: '#166534', fontWeight: 900, mb: 1 }}>
                  LIBRE DE RESTRICCIÓN
                </Typography>
                <Typography variant="body1" sx={{ color: '#166534', fontWeight: 600 }}>
                  Explotación validada para transporte y movimientos.
                </Typography>
              </>
            )}
          </Card>
        </Grid>

        {/* 2. Census Card with Donut Chart */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: '24px', p: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Censo: {stats?.totalAnimales} animales en total
              </Typography>
              <Box sx={{ height: 200, display: 'flex', alignItems: 'center' }}>
                <ResponsiveContainer width="60%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <Box sx={{ ml: 2 }}>
                  {pieData.map((entry, index) => (
                    <Stack key={index} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <FiberManualRecord sx={{ fontSize: 12, color: COLORS[index % COLORS.length] }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                        {entry.name}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* 3. Active Guides List */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: '24px', p: 1 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Guías: {stats?.ultimasGuias?.filter(g => g.estado !== 'completada').length} Guías en curso
              </Typography>
              <List sx={{ p: 0 }}>
                {stats?.ultimasGuias?.slice(0, 3).map((guia, i) => (
                  <ListItem 
                    key={guia.id} 
                    sx={{ 
                      mb: 1.5, 
                      borderRadius: '12px', 
                      border: '1px solid #e2e8f0',
                      '&:hover': { bgcolor: '#f1f5f9' }
                    }}
                  >
                    <ListItemText 
                      primary={`Guía ${guia.id}: ${guia.destino}`} 
                      secondary={guia.estado.charAt(0).toUpperCase() + guia.estado.slice(1)}
                      primaryTypographyProps={{ fontWeight: 800, fontSize: '0.9rem' }}
                    />
                    <ChevronRight sx={{ color: '#cbd5e1' }} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Bottom Row */}
        {/* 4. Issued Guides Summary */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: '24px', p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 3 }}>
              Guías: {stats?.totalAnimales || 494} Guías emitidas
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <Avatar sx={{ bgcolor: '#f1f5f9', width: 100, height: 100, borderRadius: '24px' }}>
                <ListAlt sx={{ fontSize: 60, color: '#64748b' }} />
              </Avatar>
            </Box>
          </Card>
        </Grid>

        {/* 5. Alternative Guides Status View */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: '24px', p: 1 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 2 }}>
                Guías: {stats?.ultimasGuias?.filter(g => g.estado !== 'completada').length} Guías en curso
              </Typography>
              <List sx={{ p: 0 }}>
                {stats?.ultimasGuias?.slice(0, 3).map((guia, i) => (
                  <ListItem 
                    key={guia.id} 
                    sx={{ 
                      mb: 1.5, 
                      borderRadius: '12px', 
                      border: '1px solid #e2e8f0'
                    }}
                  >
                    <ListItemText 
                      primary={`Guía ${guia.id}: ${guia.destino}`} 
                      secondary={guia.estado.charAt(0).toUpperCase() + guia.estado.slice(1)}
                      primaryTypographyProps={{ fontWeight: 800, fontSize: '0.9rem' }}
                    />
                    <ListItemIcon sx={{ minWidth: 'auto', ml: 1 }}>
                      <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
                    </ListItemIcon>
                    <ChevronRight sx={{ color: '#cbd5e1' }} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* 6. Crotales Available Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', borderRadius: '24px', p: 3, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 4 }}>
              Crotales: {stats?.crotalesDisponibles} crotales disponibles
            </Typography>
            <Typography variant="h1" sx={{ fontWeight: 900, color: '#1e293b', fontSize: '100px', my: 2 }}>
              {stats?.crotalesDisponibles}
            </Typography>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardResumen;
